/**
 * Form "Kết nối OpenCore" của tenant (mới, không có ở native; ADR-0103 mục 5): bản nháp từ cấu hình máy chủ, kiểm
 * cùng luật với `parseKetNoi` của `modules/rpa/backend/src/ket-noi/ket-noi.service.ts`, và thân `PUT /connection`
 * chỉ mang trường đã đổi.
 *
 * JWT dịch vụ **chỉ ghi**: máy chủ chỉ báo `hasJwt`. Ô trống là giữ JWT đang có; gỡ JWT là một lệnh riêng có xác
 * nhận (`PUT { jwt: null }`), không đi qua bản nháp.
 */
import type { KetNoi, ThanSuaKetNoi } from '../types.ts';

export const MAU_URL = /^(https?|wss?|grpcs?):\/\/[^\s/]+/i;
const MAU_COLLECTION = /^[A-Za-z0-9_]{1,80}$/;
const MAU_ROLE = /^[A-Za-z0-9_.-]{1,120}$/;
const MAU_TIEN_TO = /^[A-Za-z0-9_.-]{1,60}$/;

/** Khoảng của các trường số — cùng `parseKetNoi`. */
export const KHOANG = {
  projectWorkflowRights: [0, 65535],
  projectProRights: [0, 65535],
  rpcTimeoutSeconds: [30, 86400],
  maxRetry: [0, 10],
  licenseSyncIntervalMinutes: [1, 1440],
} as const;

type TruongSo = keyof typeof KHOANG;
type TruongChu = 'workflowsCollection' | 'robotsCollection' | 'licenseRole' | 'projectRolePrefix';

export interface BanNhapKetNoi {
  url: string;
  /** Rỗng = gửi lệnh qua chính `url`. */
  sdkUrl: string;
  /** JWT mới vừa gõ; rỗng = giữ JWT đang có. */
  jwt: string;
  workflowsCollection: string;
  robotsCollection: string;
  licenseRole: string;
  projectRolePrefix: string;
  /** `null` khi ô số bị xoá trắng. */
  projectWorkflowRights: number | null;
  projectProRights: number | null;
  rpcTimeoutSeconds: number | null;
  maxRetry: number | null;
  licenseSyncIntervalMinutes: number | null;
  enabled: boolean;
}

/** Giá trị máy chủ đặt khi tenant khai lần đầu mà không gửi trường đó (`coalesce` trong `INSERT`). */
export const MAC_DINH: BanNhapKetNoi = {
  url: '',
  sdkUrl: '',
  jwt: '',
  workflowsCollection: 'openrpa',
  robotsCollection: 'users',
  licenseRole: 'FOXAI_RPA_LICENSED',
  projectRolePrefix: 'FOXAI_PRJ_',
  projectWorkflowRights: 18,
  projectProRights: 65535,
  rpcTimeoutSeconds: 3600,
  maxRetry: 2,
  licenseSyncIntervalMinutes: 5,
  enabled: true,
};

const TRUONG_SO = Object.keys(KHOANG) as TruongSo[];
const TRUONG_CHU: readonly TruongChu[] = ['workflowsCollection', 'robotsCollection', 'licenseRole', 'projectRolePrefix'];

export function banNhapTu(k: KetNoi | undefined): BanNhapKetNoi {
  if (!k || !k.configured) return { ...MAC_DINH };
  return {
    url: k.url,
    sdkUrl: k.sdkUrl ?? '',
    jwt: '',
    workflowsCollection: k.workflowsCollection,
    robotsCollection: k.robotsCollection,
    licenseRole: k.licenseRole,
    projectRolePrefix: k.projectRolePrefix,
    projectWorkflowRights: k.projectWorkflowRights,
    projectProRights: k.projectProRights,
    rpcTimeoutSeconds: k.rpcTimeoutSeconds,
    maxRetry: k.maxRetry,
    licenseSyncIntervalMinutes: k.licenseSyncIntervalMinutes,
    enabled: k.enabled,
  };
}

export type TruongKetNoi = 'url' | 'sdkUrl' | 'jwt' | TruongChu | TruongSo;
export type LoiKetNoi = Partial<Record<TruongKetNoi, 'batBuoc' | 'saiDang' | 'saiKhoang' | 'canJwt'>>;

/**
 * Kiểm bản nháp. Khai lần đầu thì bắt buộc địa chỉ và JWT: máy chủ nhận cấu hình thiếu JWT, nhưng mọi lời gọi
 * OpenCore sau đó đều trả `503 RPA_OPENCORE_NOT_CONFIGURED` — báo ngay ở đây rẻ hơn.
 */
export function kiemKetNoi(d: BanNhapKetNoi, goc: KetNoi | undefined): LoiKetNoi {
  const loi: LoiKetNoi = {};
  const url = d.url.trim();
  if (!url) loi.url = 'batBuoc';
  else if (url.length > 500 || !MAU_URL.test(url)) loi.url = 'saiDang';
  const sdk = d.sdkUrl.trim();
  if (sdk && (sdk.length > 500 || !MAU_URL.test(sdk))) loi.sdkUrl = 'saiDang';
  const coJwt = goc?.configured ? goc.hasJwt : false;
  if (!coJwt && !d.jwt.trim()) loi.jwt = 'canJwt';
  if (d.jwt.length > 8000) loi.jwt = 'saiDang';
  const mau: Record<TruongChu, RegExp> = {
    workflowsCollection: MAU_COLLECTION,
    robotsCollection: MAU_COLLECTION,
    licenseRole: MAU_ROLE,
    projectRolePrefix: MAU_TIEN_TO,
  };
  for (const k of TRUONG_CHU) {
    const v = d[k].trim();
    if (!v) loi[k] = 'batBuoc';
    else if (!mau[k].test(v)) loi[k] = 'saiDang';
  }
  for (const k of TRUONG_SO) {
    const v = d[k];
    const [min, max] = KHOANG[k];
    if (v === null) loi[k] = 'batBuoc';
    else if (!Number.isInteger(v) || v < min || v > max) loi[k] = 'saiKhoang';
  }
  return loi;
}

/**
 * Thân `PUT /connection`. Khai lần đầu: gửi đủ mọi trường. Đã có cấu hình: chỉ gửi trường khác bản đang lưu
 * (máy chủ giữ nguyên trường vắng), `sdkUrl` xoá trắng thành `null`, JWT gõ mới thì thay.
 */
export function thanGuiKetNoi(d: BanNhapKetNoi, goc: KetNoi | undefined): ThanSuaKetNoi {
  const moi = {
    url: d.url.trim(),
    sdkUrl: d.sdkUrl.trim() || null,
    workflowsCollection: d.workflowsCollection.trim(),
    robotsCollection: d.robotsCollection.trim(),
    licenseRole: d.licenseRole.trim(),
    projectRolePrefix: d.projectRolePrefix.trim(),
    projectWorkflowRights: d.projectWorkflowRights ?? undefined,
    projectProRights: d.projectProRights ?? undefined,
    rpcTimeoutSeconds: d.rpcTimeoutSeconds ?? undefined,
    maxRetry: d.maxRetry ?? undefined,
    licenseSyncIntervalMinutes: d.licenseSyncIntervalMinutes ?? undefined,
    enabled: d.enabled,
  };
  const jwt = d.jwt.trim();
  const ra: ThanSuaKetNoi = {};
  if (!goc?.configured) {
    for (const [k, v] of Object.entries(moi) as [keyof typeof moi, unknown][]) {
      if (v !== undefined && v !== null) (ra as Record<string, unknown>)[k] = v;
    }
    if (jwt) ra.jwt = jwt;
    return ra;
  }
  const cu: typeof moi = {
    url: goc.url,
    sdkUrl: goc.sdkUrl,
    workflowsCollection: goc.workflowsCollection,
    robotsCollection: goc.robotsCollection,
    licenseRole: goc.licenseRole,
    projectRolePrefix: goc.projectRolePrefix,
    projectWorkflowRights: goc.projectWorkflowRights,
    projectProRights: goc.projectProRights,
    rpcTimeoutSeconds: goc.rpcTimeoutSeconds,
    maxRetry: goc.maxRetry,
    licenseSyncIntervalMinutes: goc.licenseSyncIntervalMinutes,
    enabled: goc.enabled,
  };
  for (const k of Object.keys(moi) as (keyof typeof moi)[]) {
    if (moi[k] !== undefined && moi[k] !== cu[k]) (ra as Record<string, unknown>)[k] = moi[k];
  }
  if (jwt) ra.jwt = jwt;
  return ra;
}
